import { createContext, useCallback, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { useOrganizationQualityMetrics } from 'shared/hooks/quality-metric.hooks';
import {
  adjustTrend,
  calculateGoalPercentage,
  calculateTrendDelta,
} from 'shared/utils/quality-metrics.utils';

export const TAB_KEYS = {
  OVERVIEW: 'overview',
  RESOURCES: 'resources',
  DEFINITION: 'definition',
};

const VALID_TABS = new Set(Object.values(TAB_KEYS));

/**
 * Screen state lives in the URL, so a view is shareable and survives a reload.
 * `filters` holds AG Grid's own filter model as JSON — the grid owns the filter
 * UI, so round-tripping its model is the only way to restore it without
 * rebuilding those controls by hand.
 */
export const QUERY_PARAMS = {
  METRIC: 'metric',
  TAB: 'tab',
  USERS: 'users',
  USERS_FILTERS: 'filters',
};

/** Stable empty defaults — a fresh `[]` would be a new identity every render. */
const NO_METRICS = [];
const NO_USER_IDS = [];

const parseIds = value => (value ? value.split(',').filter(Boolean) : []);

const parseFilterModel = value => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    // A hand-edited or truncated URL should drop the filters, not break the page.
    return null;
  }
};

const QualityMetricsManagementContext = createContext(null);

/**
 * @typedef {import('shared/api/quality-metric-rq.service.api').QualityMetric} QualityMetric
 * @typedef {import('shared/api/quality-metric-rq.service.api').TrendPoint} TrendPoint
 */

/**
 * Everything the detail panel and sidebar read. Declared here so the TypeScript
 * files under `components/` get the real shape instead of `any`.
 *
 * @typedef {object} QualityMetricsManagementContextValue
 * @property {Record<string, string>} lang
 * @property {Record<string, string>} metricLang
 * @property {QualityMetric[]} metrics
 * @property {boolean} loading
 * @property {unknown} error
 * @property {string | null} selectedMetricId
 * @property {(metricId: string) => void} setSelectedMetricId
 * @property {QualityMetric | null} selectedMetric
 * @property {string} selectedTab
 * @property {(tab: string) => void} setSelectedTab
 * @property {string[]} comparedUserIds
 * @property {(userIds: string[]) => void} setComparedUserIds
 * @property {Record<string, unknown> | null} usersFilterModel
 * @property {(model: Record<string, unknown> | null) => void} setUsersFilterModel
 * @property {ReturnType<typeof calculateTrendDelta> | null} groupTrendDelta
 * @property {TrendPoint[]} processedGroupTrends
 * @property {number} goalPercentage
 */

/**
 * Not nullable: the hook is only legal under `QualityMetricsManagementProvider`,
 * which is the whole screen, so a consumer never sees the `null` default.
 *
 * @returns {QualityMetricsManagementContextValue}
 */
export function useQualityMetricsManagementContext() {
  return useContext(QualityMetricsManagementContext);
}

/**
 * Group-level twin of `quality-metrics-context`. The clinician page is built
 * around the viewer's own `UserQualityMetric`; this screen has no "my score".
 *
 * All screen state — selected metric, tab, compared users, users-table filters
 * — is held in the URL rather than in component state, so a view can be shared
 * or reloaded. That also makes "changing the metric clears the comparison"
 * a single write instead of a cleanup effect per dependent piece of state.
 */
export function QualityMetricsManagementProvider({ children }) {
  const lang = useSelector(getLang('QUALITY_METRICS_MANAGEMENT'));
  const metricLang = useSelector(getLang('QUALITY_METRIC'));
  const [searchParams, setSearchParams] = useSearchParams();

  // The screen is root-gated and exists to administer every metric, hidden ones
  // included — they are exactly the ones someone comes here to unhide.
  const {
    data: metrics = NO_METRICS,
    isLoading,
    error,
  } = useOrganizationQualityMetrics({ includeHidden: true });

  const requestedMetricId = searchParams.get(QUERY_PARAMS.METRIC);
  const rawTab = searchParams.get(QUERY_PARAMS.TAB);
  const selectedTab = VALID_TABS.has(rawTab) ? rawTab : TAB_KEYS.OVERVIEW;
  // An unknown id (deleted metric, hand-edited URL) falls back to the first
  // metric; the param corrects itself on the next selection.
  const selectedMetric = metrics.find(({ id }) => id === requestedMetricId) ?? metrics[0] ?? null;
  const selectedMetricId = selectedMetric?.id ?? null;

  // `users` and `filters` are scoped to the metric that was asked for, so when
  // that metric is gone they describe something no longer on screen. Asking for
  // no metric at all is not that case — there is nothing they can disagree with.
  const isFallbackSelection = requestedMetricId !== null && requestedMetricId !== selectedMetricId;

  const comparedUserIds = useMemo(
    () => (isFallbackSelection ? NO_USER_IDS : parseIds(searchParams.get(QUERY_PARAMS.USERS))),
    [searchParams, isFallbackSelection],
  );
  const usersFilterModel = useMemo(
    () =>
      isFallbackSelection ? null : parseFilterModel(searchParams.get(QUERY_PARAMS.USERS_FILTERS)),
    [searchParams, isFallbackSelection],
  );

  const updateParams = useCallback(
    mutate =>
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  // Selecting a metric drops everything scoped to the previous one: compared
  // users belong to that metric, and the filter model references its columns.
  const setSelectedMetricId = useCallback(
    metricId =>
      updateParams(next => {
        next.set(QUERY_PARAMS.METRIC, metricId);
        next.delete(QUERY_PARAMS.USERS);
        next.delete(QUERY_PARAMS.USERS_FILTERS);
      }),
    [updateParams],
  );

  const setSelectedTab = useCallback(
    tab => updateParams(next => next.set(QUERY_PARAMS.TAB, tab)),
    [updateParams],
  );

  const setComparedUserIds = useCallback(
    userIds =>
      updateParams(next => {
        if (userIds.length) next.set(QUERY_PARAMS.USERS, userIds.join(','));
        else next.delete(QUERY_PARAMS.USERS);
      }),
    [updateParams],
  );

  const setUsersFilterModel = useCallback(
    model =>
      updateParams(next => {
        if (model && Object.keys(model).length)
          next.set(QUERY_PARAMS.USERS_FILTERS, JSON.stringify(model));
        else next.delete(QUERY_PARAMS.USERS_FILTERS);
      }),
    [updateParams],
  );

  const groupTrendDelta = useMemo(
    () =>
      selectedMetric
        ? calculateTrendDelta({
            trends: selectedMetric.groupTrends,
            invert: selectedMetric.invert,
          })
        : null,
    [selectedMetric],
  );

  // Interpolated months come back with `rate: null`; drop them, then flip the
  // rate for inverted metrics so the chart reads in the same direction as the
  // goal line.
  const processedGroupTrends = useMemo(() => {
    if (!selectedMetric?.groupTrends) return [];
    return selectedMetric.groupTrends
      .filter(({ rate }) => rate !== null)
      .map(adjustTrend(selectedMetric.invert));
  }, [selectedMetric]);

  const goalPercentage = useMemo(
    () =>
      selectedMetric
        ? calculateGoalPercentage({
            groupGoal: selectedMetric.groupGoal,
            invert: selectedMetric.invert,
            digits: 1,
          })
        : 0,
    [selectedMetric],
  );

  const value = useMemo(
    () => ({
      lang,
      metricLang,
      metrics,
      loading: isLoading,
      error,
      selectedMetricId,
      setSelectedMetricId,
      selectedMetric,
      selectedTab,
      setSelectedTab,
      comparedUserIds,
      setComparedUserIds,
      usersFilterModel,
      setUsersFilterModel,
      groupTrendDelta,
      processedGroupTrends,
      goalPercentage,
    }),
    [
      lang,
      metricLang,
      metrics,
      isLoading,
      error,
      selectedMetricId,
      setSelectedMetricId,
      selectedMetric,
      selectedTab,
      setSelectedTab,
      comparedUserIds,
      setComparedUserIds,
      usersFilterModel,
      setUsersFilterModel,
      groupTrendDelta,
      processedGroupTrends,
      goalPercentage,
    ],
  );

  return (
    <QualityMetricsManagementContext.Provider value={value}>
      {children}
    </QualityMetricsManagementContext.Provider>
  );
}

/**
 * `QualityMetricBox` and the trend helpers are written against a user metric
 * (`totalRecords` / `totalSuccess`). Map a group metric onto that shape so the
 * shared components can be reused unchanged.
 *
 * `minimumRecords` is deliberately 1, not the metric's own value: that is a
 * per-user threshold for whether a person's score counts, and applying it to
 * the group total would blank out metrics that have plenty of group data. One
 * record is the group-level threshold for being able to say anything at all,
 * and it routes a metric with no records into `MetricPropBox`'s "no data"
 * state — which is both the honest reading and the only branch of that
 * component that is legible on zero records (at 0 it renders white-on-white,
 * because it picks a background colour only when `totalRecords` is truthy).
 */
export const toGroupMetricView = metric => ({
  title: metric.title,
  totalRecords: metric.totalGroupRecords,
  totalSuccess: metric.totalGroupSuccess,
  groupGoal: metric.groupGoal,
  invert: metric.invert,
  minimumRecords: 1,
});
